/* 证据筛选：单组件多选、分页、URL 恢复及编辑器生命周期；禁止扩展为整页控制器。 */
(() => {
  if (customElements.get('bixdo-evidence-explorer')) return;

  const normalize = value => String(value || '').trim().toLowerCase();
  // FAQ 可属于多个筛选项，Liquid 使用 | 分隔；证据列表的单值继续按同一逻辑处理。
  const itemFilterValues = value => normalize(value).split('|').filter(Boolean);

  class EvidenceExplorer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.search = this.querySelector('[data-evidence-search]');
      this.filters = [...this.querySelectorAll('[data-evidence-filter]')];
      this.filterGroups = [...this.querySelectorAll('[data-evidence-filter-group]')];
      this.buttons = [...this.querySelectorAll('[data-evidence-topic]')];
      this.items = [...this.querySelectorAll('[data-evidence-item]')];
      this.status = this.querySelector('[data-evidence-status]');
      this.empty = this.querySelector('[data-evidence-empty]');
      this.list = this.querySelector('[data-evidence-list]');
      this.pagination = this.querySelector('[data-evidence-pagination]');
      this.pageButtons = this.querySelector('[data-evidence-pages]');
      this.pageStatus = this.querySelector('[data-evidence-page-status]');
      this.filterClear = this.querySelector('[data-evidence-filter-clear]');
      this.pageSize = Math.max(1, Number.parseInt(this.dataset.pageSize, 10) || 4);
      this.currentPage = 1;
      // 主实例保持原有 q/scope/topic 深链接；额外实例使用独立命名空间。
      this.namespace = this.dataset.queryNamespace ||
        (document.querySelector('bixdo-evidence-explorer') === this ? '' : this.id);

      this.addEventListener('input', event => {
        if (event.target === this.search) this.apply(true, true);
      }, options);
      this.addEventListener('change', event => {
        if (this.filters.includes(event.target)) this.apply(true, true);
      }, options);
      this.addEventListener('click', event => this.handleClick(event), options);
      this.addEventListener('shopify:block:select', event => this.revealEditorBlock(event), options);
      window.addEventListener('popstate', () => {
        this.restore();
        this.apply(false, false);
      }, options);

      this.restore();
      this.apply(false, false);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    key(name) { return this.namespace ? `${this.namespace}-${name}` : name; }

    selectedFilters() {
      const selected = Object.fromEntries(
        this.filterGroups.map(group => [group.dataset.evidenceFilterGroup, []])
      );
      this.filters.forEach(filter => {
        if (!filter.checked) return;
        const key = filter.dataset.evidenceFilter;
        if (!selected[key]) selected[key] = [];
        selected[key].push(filter.value);
      });
      if (this.buttons.length) selected.topic = this.activeTopic === 'all' ? [] : [this.activeTopic];
      return selected;
    }

    reset() {
      if (this.search) this.search.value = '';
      this.filters.forEach(filter => { filter.checked = false; });
      this.activeTopic = 'all';
      this.currentPage = 1;
    }

    restore() {
      this.reset();
      const params = new URLSearchParams(window.location.search);
      if (this.search) this.search.value = params.get(this.key('q')) || '';
      this.filters.forEach(filter => {
        const values = (params.get(this.key(filter.dataset.evidenceFilter)) || '').split(',').filter(Boolean);
        filter.checked = values.includes(filter.value);
      });
      const topic = params.get(this.key('topic'));
      if (this.buttons.some(button => button.dataset.evidenceTopic === topic)) this.activeTopic = topic;
      this.currentPage = Math.max(1, Number.parseInt(params.get(this.key('page')), 10) || 1);
    }

    handleClick(event) {
      const topic = event.target.closest('[data-evidence-topic]');
      if (topic && this.contains(topic)) {
        this.activeTopic = topic.dataset.evidenceTopic;
        this.apply(true, true);
        return;
      }

      const pageControl = event.target.closest('[data-evidence-page]');
      if (pageControl && this.contains(pageControl) && !pageControl.disabled) {
        const action = pageControl.dataset.evidencePage;
        if (action === 'previous') this.currentPage -= 1;
        else if (action === 'next') this.currentPage += 1;
        else this.currentPage = Number.parseInt(action, 10) || 1;
        this.apply(true, false);
        this.querySelector('.bixdo-ev2-section-heading')?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
        return;
      }

      if (event.target.closest('[data-evidence-reset]')) {
        this.reset();
        this.apply(true, false);
        this.search?.focus();
      }
    }

    revealEditorBlock(event) {
      const item = event.target.closest?.('[data-evidence-item]');
      this.reset();
      if (item) {
        const index = this.items.indexOf(item);
        this.currentPage = Math.floor(index / this.pageSize) + 1;
        const disclosure = item.querySelector?.('[data-evidence-disclosure]');
        if (disclosure) disclosure.open = true;
      }
      this.apply(false, false);
    }

    renderFilterState(selected) {
      this.filterGroups.forEach(group => {
        const count = selected[group.dataset.evidenceFilterGroup]?.length || 0;
        const output = group.querySelector('[data-evidence-filter-count]');
        if (!output) return;
        output.textContent = String(count);
        output.hidden = count === 0;
      });
      const hasActiveFilter = Boolean(normalize(this.search?.value)) ||
        Object.values(selected).some(values => values.length);
      if (this.filterClear) this.filterClear.hidden = !hasActiveFilter;
    }

    renderPagination(totalPages) {
      if (!this.pagination) return;
      this.pagination.hidden = totalPages <= 1;
      if (totalPages <= 1) return;

      const previous = this.pagination.querySelector('[data-evidence-page="previous"]');
      const next = this.pagination.querySelector('[data-evidence-page="next"]');
      if (previous) previous.disabled = this.currentPage === 1;
      if (next) next.disabled = this.currentPage === totalPages;
      if (this.pageStatus) this.pageStatus.textContent = `${this.currentPage} / ${totalPages}`;
      if (!this.pageButtons) return;

      this.pageButtons.replaceChildren();
      const visiblePages = new Set([1, totalPages, this.currentPage - 1, this.currentPage, this.currentPage + 1]);
      const pages = [...visiblePages].filter(page => page >= 1 && page <= totalPages).sort((a, b) => a - b);
      pages.forEach((page, index) => {
        if (index && page - pages[index - 1] > 1) {
          const ellipsis = document.createElement('span');
          ellipsis.className = 'bixdo-ev2-pagination__ellipsis';
          ellipsis.textContent = '…';
          ellipsis.setAttribute('aria-hidden', 'true');
          this.pageButtons.append(ellipsis);
        }
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.evidencePage = String(page);
        button.textContent = String(page);
        button.setAttribute('aria-label', `Page ${page}`);
        button.setAttribute('aria-controls', this.list?.id || '');
        if (page === this.currentPage) button.setAttribute('aria-current', 'page');
        this.pageButtons.append(button);
      });
    }

    updateUrl(term, selected) {
      const url = new URL(window.location.href);
      const values = { q: term, ...selected, page: this.currentPage > 1 ? String(this.currentPage) : '' };
      for (const [key, value] of Object.entries(values)) {
        const serialized = Array.isArray(value) ? value.join(',') : value;
        if (!serialized) url.searchParams.delete(this.key(key));
        else url.searchParams.set(this.key(key), serialized);
      }
      // 保留 view/preview_theme_id 等预览参数，不再误删报告页的 topic。
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }

    apply(updateUrl, resetPage) {
      if (resetPage) this.currentPage = 1;
      const term = normalize(this.search?.value);
      const selected = this.selectedFilters();
      const matches = this.items.filter(item => {
        const matchesTerm = !term || normalize(item.textContent).includes(term);
        const matchesFilters = Object.entries(selected).every(([key, values]) =>
          values.length === 0 || itemFilterValues(item.dataset[key]).some(value => values.includes(value))
        );
        return matchesTerm && matchesFilters;
      });

      const totalPages = Math.max(1, Math.ceil(matches.length / this.pageSize));
      this.currentPage = Math.min(Math.max(1, this.currentPage), totalPages);
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const currentItems = matches.slice(startIndex, startIndex + this.pageSize);
      const visibleItems = new Set(currentItems);
      this.items.forEach(item => {
        item.hidden = !visibleItems.has(item);
        item.classList.toggle('is-page-last', item === currentItems.at(-1));
      });

      this.buttons.forEach(button => {
        const active = button.dataset.evidenceTopic === this.activeTopic;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.renderFilterState(selected);
      this.renderPagination(Math.ceil(matches.length / this.pageSize));
      if (this.empty) this.empty.hidden = matches.length !== 0;
      if (this.list) this.list.hidden = matches.length === 0;
      if (this.status) {
        if (!matches.length) this.status.textContent = '0 results';
        else {
          const first = startIndex + 1;
          const last = Math.min(startIndex + this.pageSize, matches.length);
          const range = first === last ? String(first) : `${first}-${last}`;
          this.status.textContent = `${range} of ${matches.length} ${matches.length === 1 ? 'result' : 'results'}`;
        }
      }
      if (updateUrl) this.updateUrl(term, selected);
    }
  }

  customElements.define('bixdo-evidence-explorer', EvidenceExplorer);
})();
